import { useContext } from "react";
import { Box, Link, SxProps, Typography } from "@mui/material";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { getGraceDaysLeft } from "src/business/subscription/subscriptionStatus";
import { useSubscriptionTier } from "src/data/subscription/subscriptionTier";
import { isPaymentSurfaceEnabled } from "src/helpers/FeatureCapabilityConfig";
import {
  colorBorderWarning,
  colorSurfaceWarning,
  colorTextWarning,
} from "src/design/tokens";
import { RestaurantContext } from "src/Utils/Context/RestaurantContext";
import { UserContext } from "src/Utils/Context/UserContext";

interface SubscriptionExpiryBannerProps {
  /** Offset from the top of the viewport the banner sticks to. */
  topOffset: number;
  sx?: SxProps;
}

/**
 * Warns the restaurant's own staff that its subscription has run out and access
 * is only kept alive by the grace period. `subscriptionExpired` comes from
 * `GET /web/restaurants` and is omitted for admins, who are not subject to the
 * subscription gate — so the banner never shows for them.
 */
export const SubscriptionExpiryBanner = ({
  topOffset,
  sx,
}: SubscriptionExpiryBannerProps) => {
  const { t } = useTranslation();
  const { user } = useContext(UserContext);
  const { restaurants } = useContext(RestaurantContext);
  const { pathname } = useLocation();

  // The restaurant being viewed, when the path is bound to one — anything else
  // (my-organization, the admin panel…) simply does not match a known id.
  const restaurantId = pathname.split("/")[2] ?? "";
  const restaurant = restaurants.find((entry) => entry.id === restaurantId);
  const isExpired = !user?.admin && !!restaurant?.subscriptionExpired;

  // The expiration date is not on the list payload; the subscription record has
  // it, and the grace period keeps this endpoint reachable.
  const { data: subscription } = useSubscriptionTier(
    isExpired ? restaurantId : "",
  );

  if (!isExpired || !restaurant) return null;

  const daysLeft = subscription?.nextBillingCycle
    ? getGraceDaysLeft(subscription.nextBillingCycle)
    : null;

  const message =
    daysLeft === null
      ? t("subscriptionBanner.messageUnknown", { restaurant: restaurant.name })
      : daysLeft === 0
        ? t("subscriptionBanner.messageToday", { restaurant: restaurant.name })
        : t("subscriptionBanner.message", {
            count: daysLeft,
            restaurant: restaurant.name,
          });

  return (
    <Box
      sx={{
        position: "sticky",
        top: `${topOffset}px`,
        zIndex: (theme) => theme.zIndex.drawer - 1,
        display: "flex",
        alignItems: "flex-start",
        gap: 1.5,
        px: { xs: 2, md: 4 },
        py: 1.5,
        backgroundColor: colorSurfaceWarning,
        borderBottom: `1px solid ${colorBorderWarning}`,
        color: colorTextWarning,
        ...sx,
      }}
    >
      <WarningAmberOutlinedIcon fontSize="small" sx={{ mt: "2px" }} />
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {t("subscriptionBanner.title")}
        </Typography>
        <Typography variant="body2">{message}</Typography>
        {subscription?.nextBillingCycle && (
          <Typography variant="caption" sx={{ display: "block" }}>
            {t("subscriptionBanner.expiredOn", {
              date: dayjs(subscription.nextBillingCycle).format("MMMM D, YYYY"),
            })}
          </Typography>
        )}
        {/* The Payments page is the one surface a lapsed restaurant can still
            reach, and this banner is where it is being told it lapsed. */}
        {isPaymentSurfaceEnabled(user) && (
          <Link
            component={RouterLink}
            to={`/restaurant/${restaurantId}/payments?declare=renewal`}
            variant="body2"
            sx={{ color: colorTextWarning, fontWeight: 700 }}
          >
            {t("subscriptionBanner.renewNow")}
          </Link>
        )}
      </Box>
    </Box>
  );
};
